import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { setSeo, setJsonLd, clearJsonLd, track } from "@/lib/api";
import { OfferGrid } from "@/components/site/OfferGrid";
import { MmImage, useMedicalFavicon } from "@/components/medical/MedicalChrome";
import { PartnershipInvite } from "@/components/medical/MedicalInfoBlocks";
import {
  MedicalBottomCta,
  useMedicalOffers,
} from "@/components/medical/MedicalPartnerCtas";
import {
  MM_ABOUT,
  MM_AFFILIATE_NOTE,
  MM_BRAND,
  MM_CATEGORIES,
  MM_DISCLAIMER,
  MM_FEATURED_CATEGORIES,
  MM_HERO_IMAGE,
  MM_INTRO,
  MM_ROOT,
  MM_WHY,
  mmPath,
} from "@/data/medicalMobility";

export const MedicalDisclaimer = ({ testId = "mm-disclaimer" }) => (
  <section className="mt-16 border border-line bg-white px-6 md:px-9 py-8">
    <div className="flex items-start gap-3">
      <Info size={17} className="shrink-0 mt-[3px] text-sage-dark" />
      <div>
        <h2 className="text-[15px] font-semibold tracking-[0.02em] text-charcoal">
          Important information
        </h2>
        <p data-testid={testId} className="text-[14.5px] leading-[1.65] text-[#4F4B45] mt-2.5">
          {MM_DISCLAIMER}
        </p>
        <p
          data-testid={`${testId}-affiliate`}
          className="text-[14.5px] leading-[1.65] text-[#4F4B45] mt-3"
        >
          {MM_AFFILIATE_NOTE}
        </p>
      </div>
    </div>
  </section>
);

const CategoryCard = ({ category, index }) => (
  <Link
    to={mmPath(category.slug)}
    data-testid={`mm-category-card-${category.slug}`}
    className="group block bg-white border border-line hover:border-sage transition-colors duration-300"
  >
    <div className="overflow-hidden aspect-[4/3]">
      <MmImage
        base={category.image.base}
        alt={category.image.alt}
        priority={index === 0}
        sizes="(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
        className="w-full h-full object-cover"
      />
    </div>
    <div className="px-6 md:px-7 py-7">
      <span className="text-[11px] tracking-[0.18em] uppercase text-sage-dark font-semibold">
        {category.eyebrow}
      </span>
      <h3 className="display text-[24px] md:text-[27px] leading-[1.15] text-charcoal mt-3">
        {category.name}
      </h3>
      <p className="text-[15.5px] leading-[1.6] text-[#4F4B45] mt-3">{category.cardText}</p>
      <span className="inline-flex items-center gap-2 mt-6 text-[12px] tracking-[0.14em] uppercase font-semibold text-charcoal group-hover:text-sage-dark transition-colors">
        Explore Category
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

export default function MedicalHome() {
  const featuredOffers = useMedicalOffers(MM_FEATURED_CATEGORIES);
  useMedicalFavicon();

  useEffect(() => {
    setSeo({
      title: `Medical & Mobility — ${MM_BRAND.tagline} | ${MM_BRAND.name}`,
      description:
        "Explore premium home medical, mobility and accessibility equipment — home elevators, " +
        "medical beds, power mobility, home care support and respiratory " +
        "equipment — organised by category.",
      image: `${window.location.origin}/media/medical-mobility/home-elevators-accessibility-1024.webp`,
    });
    setJsonLd("mm-schema", {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: `${MM_BRAND.name} — ${MM_BRAND.division}`,
      description: MM_INTRO,
      url: window.location.origin + MM_ROOT,
      isPartOf: { "@type": "WebSite", name: MM_BRAND.name, url: window.location.origin },
      hasPart: MM_CATEGORIES.map((c) => ({
        "@type": "WebPage",
        name: c.name,
        url: window.location.origin + mmPath(c.slug),
      })),
    });
    track({ event_type: "page_view", page: MM_ROOT });
    return () => clearJsonLd("mm-schema");
  }, []);

  return (
    <div data-testid="mm-home">
      <section className="relative bg-charcoal h-[62vh] min-h-[460px] max-h-[720px]">
        <MmImage
          base={MM_HERO_IMAGE.base}
          alt={MM_HERO_IMAGE.alt}
          priority
          sizes="100vw"
          className="absolute inset-0 w-full h-full object-cover object-[50%_38%]"
        />
        <div className="absolute inset-x-0 bottom-0 h-[78%] bg-gradient-to-t from-charcoal/95 via-charcoal/72 to-transparent" />
        <div className="relative h-full max-w-[1280px] mx-auto px-5 md:px-8 flex flex-col justify-end pb-12 md:pb-16 [text-shadow:0_1px_20px_rgba(20,20,20,0.6)]">
          <span
            data-testid="mm-hero-eyebrow"
            className="text-[11px] md:text-[12px] tracking-[0.22em] uppercase text-[#BFE6E9] font-semibold"
          >
            {MM_BRAND.name}
          </span>
          <h1
            data-testid="mm-hero-heading"
            className="display text-[42px] sm:text-[56px] lg:text-[68px] leading-[1.02] text-white mt-4"
          >
            Medical &amp; Mobility
          </h1>
          <p
            data-testid="mm-hero-tagline"
            className="text-base md:text-lg tracking-[0.04em] text-[#E6E3DE] mt-4 max-w-xl"
          >
            {MM_BRAND.tagline}
          </p>
          <Link
            to={`${MM_ROOT}#categories`}
            data-testid="mm-hero-cta"
            className="inline-flex items-center gap-2.5 mt-8 self-start w-fit px-7 sm:px-8 py-4 text-[11.5px] sm:text-[12.5px] tracking-[0.14em] uppercase font-semibold bg-white text-charcoal hover:bg-sage hover:text-white transition-colors duration-300"
          >
            Explore Equipment Categories <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-14 md:py-16">
        <p
          data-testid="mm-intro"
          className="text-[17.5px] md:text-[20px] font-light leading-[1.55] text-[#3F3F3F] max-w-3xl"
        >
          {MM_INTRO}
        </p>

        <section
          id="featured-partners"
          data-testid="mm-featured-partners"
          className="mt-12 md:mt-14 scroll-mt-28"
        >
          <span className="eyebrow">Approved partners</span>
          <h2 className="display text-[30px] md:text-[40px] leading-[1.1] text-charcoal mt-5 max-w-2xl">
            Featured Medical &amp; Mobility Partners
          </h2>
          {/* Driven entirely by the offers in the admin: nothing renders until a partner is
              approved, active and carries a tracking URL. */}
          <OfferGrid
            category={MM_FEATURED_CATEGORIES}
            heading=""
            placement="MEDICAL LANDING FEATURED"
            rel="sponsored noopener noreferrer"
            ctaBlock
            disclosure
          />
        </section>

        <section
          id="categories"
          className="mt-20 md:mt-24 border-t border-line pt-12 md:pt-14 scroll-mt-28"
        >
          <span className="eyebrow">Equipment categories</span>
          <div
            data-testid="mm-category-grid"
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7 mt-7"
          >
            {MM_CATEGORIES.filter((c) => !c.hideFromGrid).map((c, i) => (
              <CategoryCard key={c.slug} category={c} index={i} />
            ))}
          </div>
        </section>

        <section className="mt-20 md:mt-24 border-t border-line pt-12 md:pt-14">
          <span className="eyebrow">{MM_WHY.eyebrow}</span>
          <h2 className="display text-[30px] md:text-[40px] leading-[1.1] text-charcoal mt-5 max-w-2xl">
            {MM_WHY.heading}
          </h2>
          <p className="text-[16.5px] md:text-[17.5px] leading-[1.6] text-[#454545] mt-5 max-w-3xl">
            {MM_WHY.body}
          </p>
          <div
            data-testid="mm-why-points"
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-9 mt-11"
          >
            {MM_WHY.points.map((p) => (
              <div key={p.h}>
                <h3 className="text-[16.5px] font-semibold text-charcoal">{p.h}</h3>
                <p className="text-[15.5px] leading-[1.6] text-[#4F4B45] mt-2.5">{p.p}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          id="about"
          data-testid="mm-about"
          className="mt-20 md:mt-24 border-t border-line pt-12 md:pt-14 scroll-mt-28"
        >
          <div className="grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16">
            <div>
              <span className="eyebrow">{MM_ABOUT.eyebrow}</span>
              <h2 className="display text-[30px] md:text-[38px] leading-[1.1] text-charcoal mt-5">
                {MM_ABOUT.heading}
              </h2>
            </div>
            <div>
              {MM_ABOUT.body.map((t) => (
                <p key={t} className="text-[16.5px] leading-[1.65] text-[#454545] mb-5">
                  {t}
                </p>
              ))}
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <Link
                  to="/contact?ref=medical-mobility"
                  data-testid="mm-about-contact"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 text-[12.5px] tracking-[0.14em] uppercase font-semibold bg-sage text-white hover:bg-sage-dark transition-colors duration-300"
                >
                  Contact Us <ArrowRight size={15} />
                </Link>
                <Link
                  to="/contact?topic=partnership&ref=medical-mobility"
                  data-testid="mm-about-partnership"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 text-[12.5px] tracking-[0.14em] uppercase font-semibold border border-charcoal text-charcoal hover:bg-charcoal hover:text-white transition-colors duration-300"
                >
                  Partnership Enquiries
                </Link>
              </div>
            </div>
          </div>
        </section>

        <MedicalBottomCta offers={featuredOffers} category={MM_FEATURED_CATEGORIES} />

        <PartnershipInvite variant="full" testId="mm-partnership-landing" />

        <MedicalDisclaimer />
      </div>
    </div>
  );
}
